/* @flow */

import { type RouterHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import * as F from 'lodash/fp';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useContext, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@sonyged/proc-ui';
import { Button, Footer, Page, PageContainer, Panel, ProcModal, genRuby } from '@sonyged/ext-proc';
import { css } from '@styled-system/css';

import { ArrowIcon, LossColor } from '../../../common/components/ArrowIcon';
import type { ContestType, Dispatch, StoreState } from '../../redux/types';
import type { RankingForCourseContent, Scoreboard } from '../../redux/reducer/ranking';
import { ScoreBoard } from './RankingPage';
import { contest } from '../../redux/selector';
import { finishActivityBegin } from '../../redux/reducer/activity';
import { makeT, themeOf } from './utils';
import AppContext from '../../AppContext';
import IceTrophy from '../../../assets/images/pages/ice/fields/fi_ice-trophy@3x.png';
import PizzaTrophy from '../../../assets/images/pages/pizza/fields/fi_piza-trophy@3x.png';
import SushiTrophy from '../../../assets/images/pages/sushi/fi_sushi-trophy@3x.png';

const dict = {
  _: {
    trophySrc: IceTrophy,
    title: 'さいこうきろく こうしん',
    yen: 'えん',
    thisMonth: 'こんげつ',
    japanFiscalYear: 'いままで',
    thisTimeResult: 'こんかいの けっか',
    viewRanking: 'ランキングを 見る',
  },
  pizza: {
    trophySrc: PizzaTrophy,
    yen: '円',
    thisTimeResult: '[今回|こんかい]の けっか',
    viewRanking: 'ランキングを 見る',
  },
  sushi: {
    trophySrc: SushiTrophy,
    yen: '円',
    thisMonth: '今月',
    japanFiscalYear: '今まで',
    thisTimeResult: '今回のけっか',
    viewRanking: 'ランキングを見る',
  },
  'ice-a': {
    trophySrc: IceTrophy,
    title: 'さいこうきろく！',
    yen: '円',
    thisMonth: '今月',
    japanFiscalYear: '今まで',
    thisTimeResult: '今回のけっか',
    viewRanking: 'ランキングを見る',
  },
  'pizza-a': {
    trophySrc: PizzaTrophy,
    title: '最高記録こうしん',
    yen: '円',
    thisMonth: '今月',
    japanFiscalYear: '今まで',
    thisTimeResult: '今回の結果',
    viewRanking: 'ランキングを見る',
  },
  'sushi-a': {
    trophySrc: SushiTrophy,
    title: '最高記録こうしん',
    yen: '円',
    thisMonth: '今月',
    japanFiscalYear: '今まで',
    thisTimeResult: '今回の結果',
    viewRanking: 'ランキングを見る',
  },
};
const t = makeT(dict);

// {{{ Confetti
class Confetti {
  ctx: CanvasRenderingContext2D;
  SIZE: number;
  x: number;
  y: number;
  alpha: number;
  _r: number;
  _g: number;
  _b: number;
  _omega: number;
  _fallTheta: number;
  _fallSpeed: number;
  _theta: number;
  _Ax: number;
  _Ay: number;
  _Az: number;
  _Bx: number;
  _By: number;
  _Bz: number;
  _Cx: number;
  _Cy: number;
  _Cz: number;

  constructor(ctx: CanvasRenderingContext2D, _size, color) {
    this.ctx = ctx;
    this.SIZE = _size;

    this.x = 0;
    this.y = 0;
    this.alpha = 1;

    this._r = color[0];
    this._g = color[1];
    this._b = color[2];

    this._omega = ((Math.random() * 2 - 1) * Math.PI) / 4;
    this._fallTheta = 0;
    this._fallSpeed = 1 + Math.random() * 2;

    this._theta = Math.random() * Math.PI * 2;
    this._Ax = 1;
    this._Ay = Math.random();
    this._Az = Math.random() * 2 - 1;
    var _l = Math.sqrt(this._Ax * this._Ax + this._Ay * this._Ay + this._Az * this._Az);
    this._Ax /= _l;
    this._Ay /= _l;
    this._Az /= _l;
    var _s = Math.sqrt(this._Ax * this._Ax + this._Ay * this._Ay);
    if (_s == 0) {
      // then A == ( 0, 0, -1 );
      this._Bx = 1.0;
      this._By = 0.0;
      this._Bz = 0.0;
      this._Cx = 0.0;
      this._Cy = 1.0;
      this._Cz = 0.0;
    } else {
      this._Bx = this._Ay;
      this._By = -this._Ax;
      this._Bz = 0;
      this._Cx = this._Ax * this._Az;
      this._Cy = this._Ay * this._Az;
      this._Cz = -(_s * _s);
      this._Bx /= _s;
      this._By /= _s;
      this._Cx /= _s * _l;
      this._Cy /= _s * _l;
      this._Cz /= _s * _l;
    }
  }

  get rotation3D() {
    return this._theta - Math.PI * 2 * Math.floor(this._theta / (Math.PI * 2));
  }

  set rotation3D(theta) {
    this._theta = theta - Math.PI * 2 * Math.floor(theta / (Math.PI * 2));
    var _cos = Math.cos(this._theta);
    var _sin = Math.sin(this._theta);

    // vector F is the rotated image of (1,0,0);
    var _Fx = this._Ax * this._Ax + (this._Bx * this._Bx + this._Cx * this._Cx) * _cos;
    var _Fy =
      this._Ax * this._Ay +
      (this._Bx * this._By + this._Cx * this._Cy) * _cos +
      (this._Bx * this._Cy - this._Cx * this._By) * _sin;
    // vector G is the rotated image of (0,1,0);
    var _Gx =
      this._Ax * this._Ay +
      (this._By * this._Bx + this._Cy * this._Cz) * _cos +
      (this._By * this._Cx - this._Cy * this._Bx) * _sin;
    var _Gy = this._Ay * this._Ay + (this._By * this._By + this._Cy * this._Cy) * _cos;

    //
    //ctx.fillStyle = 'rgba('+this._r+', '+this._g+', '+this._b+', '+this.alpha+')';
    this.ctx.fillStyle = 'rgb(' + this._r + ', ' + this._g + ', ' + this._b + ')';

    this.ctx.beginPath();
    this.ctx.lineTo(
      this.x + (-_Fx * this.SIZE) / 2 + (_Gx * this.SIZE) / 2,
      this.y + (-_Fy * this.SIZE) / 2 + (_Gy * this.SIZE) / 2
    );
    this.ctx.lineTo(
      this.x + (-_Fx * this.SIZE) / 2 - (_Gx * this.SIZE) / 2,
      this.y + (-_Fy * this.SIZE) / 2 - (_Gy * this.SIZE) / 2
    );
    this.ctx.lineTo(
      this.x + (_Fx * this.SIZE) / 2 - (_Gx * this.SIZE) / 2,
      this.y + (_Fy * this.SIZE) / 2 - (_Gy * this.SIZE) / 2
    );
    this.ctx.lineTo(
      this.x + (_Fx * this.SIZE) / 2 + (_Gx * this.SIZE) / 2,
      this.y + (_Fy * this.SIZE) / 2 + (_Gy * this.SIZE) / 2
    );
    this.ctx.closePath();
    this.ctx.fill();
  }

  fall() {
    this.rotation3D = this.rotation3D + this._omega;

    this.x += this._fallSpeed * Math.sin(this._fallTheta);
    this.y += this._fallSpeed * Math.cos(this._fallTheta);
    this._fallTheta += ((Math.random() * 2 - 1) * Math.PI) / 12;
    if (this._fallTheta < -Math.PI / 2) {
      this._fallTheta = -Math.PI - this._fallTheta;
    }
    if (this._fallTheta > Math.PI / 2) {
      this._fallTheta = Math.PI - this._fallTheta;
    }
  }
}

class Confetties {
  static DEF_KAMIKIRE_MAX = 40;

  static COLORS = [
    [240, 120, 77],
    [255, 225, 92],
    [70, 204, 224],
    [252, 172, 63],
  ];

  cvs: HTMLCanvasElement | null;
  stageWidth: number;
  stageHeight: number;
  ctx: ?CanvasRenderingContext2D;
  intervalID: ?IntervalID;
  confetties: Confetti[];
  resizeFlg: boolean;

  constructor() {
    this.stop();
  }

  start() {
    this.stop();
    const cvs = (document.getElementById('confetti'): any);
    this.cvs = cvs;

    //ウィンドウサイズ設定
    this.stageWidth = window.innerWidth ? window.innerWidth : window.parent.screen.width;
    this.stageHeight = window.innerHeight ? window.innerHeight : window.parent.screen.height;
    this.cvs.width = this.stageWidth;
    this.cvs.height = this.stageHeight;

    const ctx = this.cvs.getContext('2d');
    this.ctx = ctx;
    this.ctx.fillStyle = '#FFF';
    this.ctx.fillRect(0, 0, cvs.width, cvs.height);

    for (var i = 0; i < Confetties.DEF_KAMIKIRE_MAX; i++) {
      var confettie = new Confetti(
        ctx,
        15 + Math.floor(Math.random() * 15),
        Confetties.COLORS[i % Confetties.COLORS.length]
      );

      confettie.x = Math.random() * this.stageWidth;
      confettie.y = Math.random() * this.stageHeight;

      ctx.fillStyle = '#' + confettie._r + confettie._g + confettie._b;
      ctx.fillRect(confettie.x, confettie.y, confettie.SIZE, confettie.SIZE);

      //
      this.confetties.push(confettie);
    }
    this.intervalID = setInterval(this.EnterFrame.bind(this), 50);
  }

  stop() {
    if (this.intervalID) {
      clearInterval(this.intervalID);
    }

    this.confetties = [];
    this.cvs = null;
    this.ctx = null;
    this.stageWidth = 0;
    this.stageHeight = 0;
    this.resizeFlg = true;
    this.intervalID = null;
  }

  resize() {
    this.resizeFlg = true;
    this.stageWidth = window.innerWidth ? window.innerWidth : window.parent.screen.width;
    this.stageHeight = window.innerHeight ? window.innerHeight : window.parent.screen.height;
  }

  EnterFrame() {
    if (this.resizeFlg && this.cvs) {
      this.resizeFlg = false;
      this.cvs.width = this.stageWidth;
      this.cvs.height = this.stageHeight;
    }
    if (this.ctx) this.ctx.clearRect(0, 0, this.stageWidth, this.stageHeight);

    for (var i = 0; i < Confetties.DEF_KAMIKIRE_MAX; ++i) {
      if (this.confetties[i].y > 0) {
        var par = this.confetties[i].y / this.stageHeight;
        par = 1 - par;

        this.confetties[i].alpha = par;
      }

      if (this.confetties[i].x - this.confetties[i].SIZE / Math.SQRT2 > this.stageWidth) {
        this.confetties[i].x -= this.stageWidth;
      }
      if (this.confetties[i].x + this.confetties[i].SIZE / Math.SQRT2 < 0) {
        this.confetties[i].x += this.stageWidth;
      }
      if (this.confetties[i].y - this.confetties[i].SIZE / Math.SQRT2 > this.stageHeight) {
        this.confetties[i].y -= this.stageHeight;
      }

      this.confetties[i].fall();
    }
  }
}
// }}}

type ConfettiesViewProps = {
  kind: ContestType,
  totalScore: number,
  avatar: string,
  onNextClick: Function,
};

const ConfettiesView = (props: ConfettiesViewProps) => {
  const context = useContext(AppContext);
  const { avatar, kind, totalScore, onNextClick } = props;

  useLayoutEffect(() => {
    context && context.soundController.sePlay('proc_finish01');
    if (totalScore < 0) return; // no confetti
    const confetti = new Confetties();
    confetti.start();

    const resizeHandler = F.debounce(100, () => {
      confetti.resize();
    });

    window.addEventListener('resize', resizeHandler);

    return () => {
      confetti.stop();
      document.removeEventListener('resize', resizeHandler);
    };
  }, [context, totalScore]);

  return (
    <div className={`completion-page-template tertiary100-bg-color ${themeOf(kind)}`}>
      <div className="completion-page-container">
        <div
          className={`flex1 completion-page-avatar completion-page-avatar--${avatar || 1} ${
            totalScore < 0 ? 'bad' : ''
          }`}
        />

        <Page.normal fullHeight={false} menu={false}>
          <PageContainer.small fullHeight={false}>
            <Panel.vertical className="completion-page-contents">
              <div className="title proc-medium-font secondary100-color">{genRuby(t(kind, 'thisTimeResult'))}</div>
              <MoneyBox isLoss={totalScore < 0} className="total-amount bold">
                {Math.abs(totalScore)}
                <span className="proc-medium-font postfix">{t(kind, 'yen')}</span>
                {(kind === 'ice-a' || kind === 'sushi-a') && (
                  <ArrowIcon forNum={totalScore} className="proc-large-font postfix" />
                )}
              </MoneyBox>
            </Panel.vertical>
          </PageContainer.small>
        </Page.normal>
        <canvas id="confetti" />
        <Footer.normal className="white-bg-color">
          <Button.attention className="next" onClick={onNextClick}>
            {t(kind, 'viewRanking')}
          </Button.attention>
        </Footer.normal>
      </div>
    </div>
  );
};

const MoneyBox = styled(Box)(({ isLoss }) =>
  css({
    color: isLoss ? LossColor : 'gray100',
  })
);

type RankingPageProps = {
  boards: Array<{ name: string, board: ?Scoreboard }>,
  isNewHighScore: boolean,
  kind: ContestType,
  onNextClick: Function,
};

const RankingPage = ({ kind, boards, onNextClick, isNewHighScore }: RankingPageProps) => {
  const [modalOpen, setModalOpen] = useState(isNewHighScore);
  const nonNullBoards: Array<{ name: string, board: Scoreboard }> = (boards.filter(x => Boolean(x.board)): any);
  return (
    <div className={`${themeOf(kind)} flex flex-column h100`}>
      <ProcModal isOpened={modalOpen} rootNodeId={`proc-${themeOf(kind)}-fields-root-page`}>
        <div key="modal" className={'proc-modal proc-modal--hint'}>
          <div className="proc-modal-container">
            <div className={'proc-modal-header'}>{t(kind, 'title')}</div>
            <div className={'proc-modal-contents gray100-color proc-font-medium'}>
              <div className={`proc-field-highscore-container proc-border-radius proc-medium-font ${themeOf(kind)}`}>
                <img alt="" src={t(kind, 'trophySrc')} />
              </div>
            </div>
          </div>
          <Footer.normal shadow={false}>
            <Button.attention
              className={'modal-close-se'}
              onClick={() => {
                setModalOpen(false);
              }}
            >
              とじる
            </Button.attention>
          </Footer.normal>
        </div>
      </ProcModal>
      <Page.fluid className="tertiary100-bg-color flex1">
        <PageContainer.normal fullHeight={false}>
          <ScoreBoard boards={nonNullBoards} kind={kind} />
        </PageContainer.normal>
      </Page.fluid>
      <Footer.normal className="white-bg-color">
        <Button.attention onClick={onNextClick}>かんりょう</Button.attention>
      </Footer.normal>
    </div>
  );
};

type OwnProps = {|
  history: RouterHistory,
  kind: ContestType, // "ice" or "pizza"
  totalScore: number,
|};

type Props = {
  ...OwnProps,
  avatar: string,
  courseContentUid: ?string,
  ranking: RankingForCourseContent,
  finishActivityBegin: typeof finishActivityBegin,
  isNewHighScore: boolean,
  loading: boolean,
};

type State = {
  page: 'confetti' | 'ranking',
};

export class CompletionPage extends React.Component<Props, State> {
  state = { page: 'confetti', isNewHighScore: false };

  componentDidMount() {
    const { courseContentUid, finishActivityBegin, totalScore } = this.props;
    if (!courseContentUid) throw new Error('Unknown course content uid');

    finishActivityBegin({ courseContentUid, point: totalScore });
  }

  render() {
    const { page } = this.state;
    const { kind, history, ranking, isNewHighScore, loading } = this.props;
    if (!loading && page === 'ranking') {
      if (!ranking) {
        history.push(`/${kind}/fields/?skip_intro=true`);
        return null;
      }
      return (
        <RankingPage
          kind={kind}
          isNewHighScore={isNewHighScore}
          onNextClick={() => {
            history.push(`/${kind}/fields/?skip_intro=true`);
          }}
          boards={[
            { name: t(kind, 'thisMonth'), board: ranking.get('monthly') },
            { name: t(kind, 'japanFiscalYear'), board: ranking.get('japanFiscalYear') },
          ]}
        />
      );
    }

    return (
      <ConfettiesView
        {...this.props}
        onNextClick={() => {
          this.setState({ page: 'ranking' });
        }}
      />
    );
  }
}

CompletionPage.propTypes = {
  kind: PropTypes.string, // "ice" or "pizza"
  totalScore: PropTypes.number,
  avatar: PropTypes.string,
};

const mapStateToProps = (state: StoreState, { kind }: OwnProps) => {
  const avatar = state.profile ? state.profile.avatar : '';
  const courseContentUid = contest[kind].fieldUid(state);
  const ranking = state.ranking.rankings.get(courseContentUid ? courseContentUid : '');
  const isNewHighScore = state.ranking.isNewHighScore;
  const loading = state.activity.loading;
  return { avatar, courseContentUid, ranking, isNewHighScore, loading };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  finishActivityBegin: payload => dispatch(finishActivityBegin(payload)),
});

export default connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps)(CompletionPage);
